// @/components/reusable/SidebarLockedBadge.jsx

/**
 * Sidebar Locked Badge Component
 *
 * A visual indicator for locked sidebar items in guided mode, showing a lock icon
 * to indicate that certain navigation items are not yet available to the user.
 *
 * Features:
 * - Simple lock icon with consistent styling
 * - Visual indicator for unavailable navigation items
 * - Used in guided mode to show progression requirements
 * - Small footprint for embedding in sidebar navigation
 * - Consistent styling with application design system
 * - Accessible SVG icon with proper aria attributes
 * - Collapsed mode shows small dot indicator
 *
 * @module components/reusable/SidebarLockedBadge
 */

import React from 'react';

/**
 * SidebarLockedBadge component
 * Shows a lock icon for locked sidebar items in guided mode
 *
 * @param {Object} props - Component props
 * @param {boolean} props.collapsed - If true, shows compact dot indicator instead of lock icon
 * @returns {React.ReactNode} Lock icon badge component
 */
const SidebarLockedBadge = ({ collapsed = false }) => {
  // Collapsed mode: small dot indicator
  if (collapsed) {
    return (
      <div className="size-2.5 rounded-full bg-zinc-300 ring-2 ring-zinc-50" />
    );
  }

  // Expanded mode: full lock icon
  return (
    <div className="flex size-5 items-center justify-center rounded-sm bg-zinc-200 text-zinc-500">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="size-3"
        aria-hidden="true">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
        />
      </svg>
    </div>
  );
};

export default SidebarLockedBadge;